import React, { useState, useContext, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../contexts/AuthContext';

// ─── Animated Space Canvas ────────────────────────────────────────────────────
function SpaceCanvas() {
  const canvasRef = useRef(null);
  const animRef   = useRef(null);
  const S         = useRef({ stars: [], comets: [], aliens: [], satellites: [], ufo: null, tick: 0 });

  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx    = canvas.getContext('2d');

    const resize = () => { canvas.width = window.innerWidth; canvas.height = window.innerHeight; };
    resize();
    window.addEventListener('resize', resize);

    const R = S.current;

    // Stars
    R.stars = Array.from({ length: 240 }, () => ({
      x: Math.random(), y: Math.random(),
      r: Math.random() * 1.4 + 0.3,
      alpha: Math.random() * 0.6 + 0.3,
      twinkleSpeed: Math.random() * 0.02 + 0.005,
      twinklePhase: Math.random() * Math.PI * 2,
    }));

    const spawnComet = () => {
      const fromTop = Math.random() > 0.4;
      return {
        x: fromTop ? Math.random() * canvas.width : -60,
        y: fromTop ? -60 : Math.random() * canvas.height * 0.5,
        vx: 5 + Math.random() * 8,
        vy: fromTop ? 4 + Math.random() * 6 : 1.5 + Math.random() * 3,
        len: 90 + Math.random() * 130,
        alpha: 0, life: 0,
        maxLife: 160 + Math.random() * 120,
        color: ['#a5f3fc','#fde68a','#c4b5fd','#86efac'][Math.floor(Math.random() * 4)],
      };
    };

    const ALIEN_EMOJIS = ['👽','🤖','👾','🛸'];
    const spawnAlien = () => {
      const goRight = Math.random() > 0.5;
      return {
        emoji: ALIEN_EMOJIS[Math.floor(Math.random() * ALIEN_EMOJIS.length)],
        x: goRight ? -70 : canvas.width + 70,
        y: 60 + Math.random() * (canvas.height - 120),
        vx: (goRight ? 1 : -1) * (0.5 + Math.random() * 0.9),
        vy: (Math.random() - 0.5) * 0.4,
        size: 26 + Math.random() * 22,
        alpha: 0, life: 0,
        maxLife: 320 + Math.random() * 220,
        wobble: Math.random() * Math.PI * 2,
        wobbleSpeed: 0.025 + Math.random() * 0.025,
      };
    };

    const spawnSatellite = () => ({
      x: -90, y: 40 + Math.random() * (canvas.height * 0.55),
      vx: 0.7 + Math.random() * 0.7, vy: (Math.random() - 0.5) * 0.25,
      angle: 0, rotSpeed: 0.018 + Math.random() * 0.02,
      alpha: 0, life: 0,
      maxLife: 480 + Math.random() * 320,
      size: 20 + Math.random() * 12,
    });

    R.comets     = Array.from({ length: 3 }, (_, i) => { const c = spawnComet(); c.life = Math.floor(Math.random() * c.maxLife * 0.6); c.x += c.vx * c.life; c.y += c.vy * c.life; return c; });
    R.aliens     = Array.from({ length: 2 }, () => { const a = spawnAlien(); a.life = Math.floor(Math.random() * a.maxLife * 0.5); a.x += a.vx * a.life; return a; });
    R.satellites = Array.from({ length: 2 }, () => { const s = spawnSatellite(); s.life = Math.floor(Math.random() * s.maxLife * 0.4); s.x += s.vx * s.life; return s; });

    const drawSatellite = (c, sat) => {
      c.save();
      c.translate(sat.x, sat.y);
      c.rotate(sat.angle);
      c.globalAlpha = sat.alpha;
      const s = sat.size;
      c.fillStyle = '#94a3b8';
      c.fillRect(-s * 0.4, -s * 0.2, s * 0.8, s * 0.4);
      c.fillStyle = '#38bdf8';
      c.fillRect(-s * 1.15, -s * 0.13, s * 0.65, s * 0.26);
      c.fillRect(s * 0.5, -s * 0.13, s * 0.65, s * 0.26);
      c.strokeStyle = '#0ea5e9'; c.lineWidth = 0.8;
      [-s * 0.82, -s * 0.65].forEach(ox => { c.beginPath(); c.moveTo(ox, -s*0.13); c.lineTo(ox, s*0.13); c.stroke(); });
      [s * 0.67, s * 0.84].forEach(ox => { c.beginPath(); c.moveTo(ox, -s*0.13); c.lineTo(ox, s*0.13); c.stroke(); });
      c.strokeStyle = '#cbd5e1'; c.lineWidth = 1.5;
      c.beginPath(); c.moveTo(0, -s * 0.2); c.lineTo(0, -s * 0.55); c.stroke();
      c.beginPath(); c.arc(0, -s * 0.55, 3, 0, Math.PI * 2);
      c.fillStyle = '#f8fafc'; c.fill();
      c.restore();
    };

    const drawUFO = (c, u, W, H, tick) => {
      c.save();
      c.translate(u.x, u.y);
      c.globalAlpha = u.alpha;
      const beam = Math.sin(u.life * 0.1) * 0.5 + 0.5;
      const bg = c.createLinearGradient(0, 18, 0, 90);
      bg.addColorStop(0, `rgba(134,239,172,${beam * 0.45})`);
      bg.addColorStop(1, 'transparent');
      c.beginPath();
      c.moveTo(-22, 18); c.lineTo(22, 18); c.lineTo(38, 90); c.lineTo(-38, 90);
      c.fillStyle = bg; c.fill();
      c.beginPath(); c.ellipse(0, 0, 19, 11, 0, Math.PI, 0);
      c.fillStyle = 'rgba(147,197,253,0.9)'; c.fill();
      c.strokeStyle = 'rgba(96,165,250,0.6)'; c.lineWidth = 1; c.stroke();
      c.beginPath(); c.ellipse(0, 5, 33, 9, 0, 0, Math.PI * 2);
      const bodyG = c.createLinearGradient(-33, 0, 33, 14);
      bodyG.addColorStop(0, '#475569'); bodyG.addColorStop(0.5, '#94a3b8'); bodyG.addColorStop(1, '#334155');
      c.fillStyle = bodyG; c.fill();
      [-18, -6, 6, 18].forEach((ox, li) => {
        c.beginPath(); c.arc(ox, 5, 2.5, 0, Math.PI * 2);
        c.fillStyle = `hsl(${(tick * 5 + li * 70) % 360},100%,65%)`; c.fill();
      });
      c.restore();
    };

    const loop = () => {
      const W = canvas.width, H = canvas.height;
      ctx.clearRect(0, 0, W, H);
      R.tick++;

      const bg = ctx.createRadialGradient(W * 0.5, H * 0.35, 0, W * 0.5, H * 0.5, W * 0.85);
      bg.addColorStop(0, '#0d0d2b');
      bg.addColorStop(0.45, '#060614');
      bg.addColorStop(1, '#020208');
      ctx.fillStyle = bg; ctx.fillRect(0, 0, W, H);

      ctx.save();
      ctx.globalAlpha = 0.055;
      const n1 = ctx.createRadialGradient(W * 0.12, H * 0.18, 0, W * 0.12, H * 0.18, W * 0.28);
      n1.addColorStop(0, '#7c3aed'); n1.addColorStop(1, 'transparent');
      ctx.fillStyle = n1; ctx.fillRect(0, 0, W, H);
      const n2 = ctx.createRadialGradient(W * 0.88, H * 0.72, 0, W * 0.88, H * 0.72, W * 0.32);
      n2.addColorStop(0, '#0e7490'); n2.addColorStop(1, 'transparent');
      ctx.fillStyle = n2; ctx.fillRect(0, 0, W, H);
      const n3 = ctx.createRadialGradient(W * 0.5, H * 0.9, 0, W * 0.5, H * 0.9, W * 0.2);
      n3.addColorStop(0, '#1d4ed8'); n3.addColorStop(1, 'transparent');
      ctx.fillStyle = n3; ctx.fillRect(0, 0, W, H);
      ctx.restore();

      R.stars.forEach(st => {
        const a = st.alpha + Math.sin(R.tick * st.twinkleSpeed + st.twinklePhase) * 0.28;
        ctx.beginPath();
        ctx.arc(st.x * W, st.y * H, st.r, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(255,255,255,${Math.max(0, Math.min(1, a))})`;
        ctx.fill();
      });

      R.comets.forEach((c, i) => {
        c.life++; c.x += c.vx; c.y += c.vy;
        c.alpha = c.life < 20 ? c.life / 20 : c.life > c.maxLife - 20 ? (c.maxLife - c.life) / 20 : 1;
        const mag = Math.hypot(c.vx, c.vy);
        const nx = c.vx / mag, ny = c.vy / mag;
        const tail = ctx.createLinearGradient(c.x, c.y, c.x - nx * c.len, c.y - ny * c.len);
        tail.addColorStop(0, `rgba(255,255,255,${c.alpha})`);
        tail.addColorStop(0.25, `${c.color}${Math.floor(c.alpha * 180).toString(16).padStart(2,'0')}`);
        tail.addColorStop(1, 'transparent');
        ctx.beginPath();
        ctx.moveTo(c.x, c.y);
        ctx.lineTo(c.x - nx * c.len, c.y - ny * c.len);
        ctx.strokeStyle = tail; ctx.lineWidth = 2; ctx.stroke();
        ctx.beginPath(); ctx.arc(c.x, c.y, 2.5, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(255,255,255,${c.alpha})`; ctx.fill();
        if (c.life >= c.maxLife || c.x > W + 120 || c.y > H + 120) R.comets[i] = spawnComet();
      });
      if (R.tick % 180 === 0 && R.comets.length < 5) R.comets.push(spawnComet());

      R.aliens.forEach((al, i) => {
        al.life++; al.x += al.vx;
        al.wobble += al.wobbleSpeed;
        al.y += al.vy + Math.sin(al.wobble) * 0.45;
        al.alpha = al.life < 45 ? al.life / 45 : al.life > al.maxLife - 45 ? (al.maxLife - al.life) / 45 : 1;
        ctx.save();
        ctx.globalAlpha = al.alpha;
        ctx.font = `${al.size}px serif`;
        ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
        ctx.fillText(al.emoji, al.x, al.y);
        ctx.restore();
        const oob = al.x < -120 || al.x > W + 120;
        if (al.life >= al.maxLife || oob) R.aliens[i] = spawnAlien();
      });
      if (R.tick % 280 === 0 && R.aliens.length < 4) R.aliens.push(spawnAlien());

      R.satellites.forEach((sat, i) => {
        sat.life++; sat.x += sat.vx; sat.y += sat.vy; sat.angle += sat.rotSpeed;
        sat.alpha = sat.life < 60 ? sat.life / 60 : sat.life > sat.maxLife - 60 ? (sat.maxLife - sat.life) / 60 : 0.82;
        drawSatellite(ctx, sat);
        if (sat.life >= sat.maxLife || sat.x > W + 130) R.satellites[i] = spawnSatellite();
      });
      if (R.tick % 380 === 0 && R.satellites.length < 3) R.satellites.push(spawnSatellite());

      if (!R.ufo && R.tick % 850 === 0) {
        R.ufo = { x: -110, y: 70 + Math.random() * (H * 0.38), vx: 2.5 + Math.random() * 2, alpha: 0, life: 0 };
      }
      if (R.ufo) {
        const u = R.ufo;
        u.life++; u.x += u.vx;
        u.alpha = u.life < 35 ? u.life / 35 : u.life > 220 ? Math.max(0, 1 - (u.life - 220) / 30) : 1;
        drawUFO(ctx, u, W, H, R.tick);
        if (u.life > 260 || u.x > W + 160) R.ufo = null;
      }

      animRef.current = requestAnimationFrame(loop);
    };

    animRef.current = requestAnimationFrame(loop);
    return () => {
      cancelAnimationFrame(animRef.current);
      window.removeEventListener('resize', resize);
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      style={{ position: 'fixed', inset: 0, width: '100%', height: '100%', zIndex: 0, display: 'block' }}
    />
  );
}

// ─── Login ─────────────────────────────────────────────────────────────────────
export default function Login() {
  const [view, setView]             = useState('login'); // login | register | forgot | forgot-sent
  const [email, setEmail]           = useState('');
  const [password, setPassword]     = useState('');
  const [tenantName, setTenantName] = useState('');
  const [error, setError]           = useState('');
  const [loading, setLoading]       = useState(false);
  const { login, register }         = useContext(AuthContext);
  const navigate                    = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    let result;
    if (view === 'register') {
      if (!tenantName || !email || !password) { setError('All fields required'); setLoading(false); return; }
      result = await register(tenantName, email, password);
    } else {
      if (!email || !password) { setError('Email and password required'); setLoading(false); return; }
      result = await login(email, password);
    }

    setLoading(false);
    if (result.success) {
      navigate(result.role === 'super_admin' ? '/admin' : '/dashboard');
    } else {
      setError(result.error);
    }
  };

  const handleForgotSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!email) { setError('Enter your email address.'); return; }
    setLoading(true);
    try {
      await fetch(`${process.env.REACT_APP_API_URL}/api/auth/forgot-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email }),
      });
      setView('forgot-sent');
    } catch {
      setError('Network error. Please try again.');
    }
    setLoading(false);
  };

  const switchView = (v) => { setView(v); setError(''); setPassword(''); };

  return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', position: 'relative', overflow: 'hidden' }}>

      <SpaceCanvas />

      <div style={{ position: 'relative', zIndex: 10, width: '100%', maxWidth: 448, padding: '0 16px' }}>
        <div className="bg-white rounded-lg shadow-2xl p-8 w-full">

          <div className="text-center mb-8">
            <h1 className="text-3xl font-bold text-gray-800">ONLYnumber</h1>
            <p className="text-gray-600 mt-2">Your number. Anywhere.</p>
          </div>

          {/* ── Forgot password sent ── */}
          {view === 'forgot-sent' && (
            <div className="text-center space-y-4">
              <p className="text-gray-700">If that email is registered, a reset link is on its way. Check your inbox.</p>
              <button
                type="button"
                onClick={() => switchView('login')}
                className="text-blue-600 hover:text-blue-700 text-sm font-medium"
              >
                Back to Sign In
              </button>
            </div>
          )}

          {/* ── Forgot password form ── */}
          {view === 'forgot' && (
            <form onSubmit={handleForgotSubmit} className="space-y-4">
              <p className="text-sm text-gray-600">Enter your email and we'll send you a reset link.</p>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none"
                  placeholder="you@example.com"
                  required
                />
              </div>
              {error && (
                <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-2 rounded">{error}</div>
              )}
              <button
                type="submit"
                disabled={loading}
                className="w-full bg-blue-600 text-white py-2 rounded-lg hover:bg-blue-700 transition disabled:opacity-50"
              >
                {loading ? 'Sending...' : 'Send Reset Link'}
              </button>
              <div className="text-center">
                <button type="button" onClick={() => switchView('login')} className="text-blue-600 hover:text-blue-700 text-sm font-medium">
                  Back to Sign In
                </button>
              </div>
            </form>
          )}

          {/* ── Login / Register form ── */}
          {(view === 'login' || view === 'register') && (
            <form onSubmit={handleSubmit} className="space-y-4">
              {view === 'register' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Organization Name</label>
                  <input
                    type="text"
                    value={tenantName}
                    onChange={(e) => setTenantName(e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none"
                    placeholder="Your Company"
                  />
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none"
                  placeholder="you@example.com"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Password</label>
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none"
                  placeholder="••••••••"
                />
                {view === 'login' && (
                  <div className="text-right mt-1">
                    <button
                      type="button"
                      onClick={() => switchView('forgot')}
                      className="text-sm text-blue-600 hover:text-blue-700"
                    >
                      Forgot password?
                    </button>
                  </div>
                )}
              </div>

              {error && (
                <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-2 rounded">{error}</div>
              )}

              <button
                type="submit"
                disabled={loading}
                className="w-full bg-blue-600 text-white py-2 rounded-lg hover:bg-blue-700 transition disabled:opacity-50"
              >
                {loading ? 'Loading...' : view === 'register' ? 'Create Account' : 'Sign In'}
              </button>
            </form>
          )}

          {(view === 'login' || view === 'register') && (
            <div className="mt-6 text-center">
              <button
                type="button"
                onClick={() => switchView(view === 'register' ? 'login' : 'register')}
                className="text-blue-600 hover:text-blue-700 text-sm font-medium"
              >
                {view === 'register' ? 'Already have an account? Sign In' : "Don't have an account? Register"}
              </button>
            </div>
          )}

        </div>
      </div>
    </div>
  );
}
